import React, { useState, useEffect } from 'react'
import { X, Calendar, Users, Building, Clock, AlertTriangle, ChevronDown } from 'lucide-react'
import { employeeShiftService } from '../../services/employeeShiftService'
import hrShiftService from '../../services/hrShiftService'
import branchService from '../../services/branchService'
import InlineLoader from '../ui/InlineLoader'

const AssignShiftModal = ({ onClose, onSuccess, jobsMap = {}, branchesMap = {} }) => {
  const [formData, setFormData] = useState({
    shift: '',
    branch: '',
    startDate: '',
    endDate: '',
    employees: []
  })
  const [shifts, setShifts] = useState([])
  const [branches, setBranches] = useState([])
  const [availableEmployees, setAvailableEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(true)
  const [errors, setErrors] = useState({})

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setFormLoading(true)
        
        // Fetch shifts and branches in parallel
        const [shiftsResponse, branchesResponse] = await Promise.all([
          hrShiftService.getAllShifts(),
          branchService.getAllBranches()
        ])

        if (shiftsResponse?.success && shiftsResponse?.data) {
          setShifts(shiftsResponse.data.shifts || [])
        }

        if (branchesResponse?.success && branchesResponse?.data) {
          setBranches(branchesResponse.data.branches || [])
        }
      } catch (error) {
        console.error('Error fetching initial data:', error)
      } finally {
        setFormLoading(false)
      }
    }

    fetchInitialData()
  }, [])

  // Fetch employees when branch changes
  useEffect(() => {
    const fetchEmployees = async () => {
      if (formData.branch) {
        try {
          // Fetch available employees (those without active shifts)
          const availableResponse = await employeeShiftService.getAvailableEmployees(formData.branch)
          
          if (availableResponse?.success && availableResponse?.data) {
            setAvailableEmployees(availableResponse.data.employees || [])
          }
        } catch (error) {
          console.error('Error fetching employees:', error)
        }
      } else {
        setAvailableEmployees([])
      }
    }

    fetchEmployees()
  }, [formData.branch])

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  // Handle employee selection
  const handleEmployeeSelection = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      employees: prev.employees.includes(employeeId)
        ? prev.employees.filter(id => id !== employeeId)
        : [...prev.employees, employeeId]
    }))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.shift) {
      newErrors.shift = 'Shift is required'
    }

    if (!formData.branch) {
      newErrors.branch = 'Branch is required'
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required'
    }

    if (formData.employees.length === 0) {
      newErrors.employees = 'At least one employee must be selected'
    }

    // Validate date logic
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate)
      const endDate = new Date(formData.endDate)
      
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)

      if (formData.employees.length > 1) {
        // Create multiple employee shifts
        const requestData = {
          shift: formData.shift,
          branch: formData.branch,
          startDate: formData.startDate,
          employees: formData.employees
        }

        if (formData.endDate) {
          requestData.endDate = formData.endDate
        }

        await employeeShiftService.createMultipleEmployeeShifts(requestData)
      } else {
        // Create single employee shift
        const requestData = {
          shift: formData.shift,
          branch: formData.branch,
          startDate: formData.startDate,
          employee: formData.employees[0]
        }

        if (formData.endDate) {
          requestData.endDate = formData.endDate
        }

        await employeeShiftService.createEmployeeShift(requestData)
      }

      onSuccess()
    } catch (error) {
      console.error('Error assigning shift:', error)
      setErrors({
        submit: error.response?.data?.message || 'Failed to assign shift'
      })
    } finally {
      setLoading(false)
    }
  }

  // Get selected shift details
  const selectedShift = shifts.find(shift => shift._id === formData.shift)

  if (formLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-center">
            <InlineLoader />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Assign Shift</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Shift Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shift Type *
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <select
                name="shift"
                value={formData.shift}
                onChange={handleInputChange}
                className={`appearance-none pl-10 pr-8 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white ${
                  errors.shift ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Shift</option>
                {shifts.map(shift => (
                  <option key={shift._id} value={shift._id}>
                    {shift.shiftName} ({shift.startTime} - {shift.endTime})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
            {errors.shift && (
              <p className="text-red-500 text-sm mt-1">{errors.shift}</p>
            )}
            
            {/* Show shift details */}
            {selectedShift && (
              <div className="mt-2 p-3 bg-orange-50 rounded-lg">
                <div className="text-sm text-orange-800">
                  <p><strong>Working Hours:</strong> {selectedShift.workingHours} hours</p>
                  <p><strong>Break Hours:</strong> {selectedShift.breakHours} hours</p>
                  {selectedShift.notes && <p><strong>Notes:</strong> {selectedShift.notes}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Branch Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Branch *
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <select
                name="branch"
                value={formData.branch}
                onChange={handleInputChange}
                className={`appearance-none pl-10 pr-8 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white ${
                  errors.branch ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Branch</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
            {errors.branch && (
              <p className="text-red-500 text-sm mt-1">{errors.branch}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`pl-10 pr-3 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.startDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className={`pl-10 pr-3 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.endDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Employee Selection */}
          {formData.branch && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Employee(s) *
              </label>
              
              {availableEmployees.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                    <p className="text-yellow-800 text-sm">
                      No employees available for assignment. All employees in this branch may already have active shifts.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                  <div className="p-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Available Employees ({availableEmployees.length})
                      </span>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.employees.length === availableEmployees.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                employees: availableEmployees.map(emp => emp._id)
                              }))
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                employees: []
                              }))
                            }
                          }}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-600">Select All</span>
                      </label>
                    </div>
                  </div>
                  
                  {availableEmployees.map(employee => (
                    <div key={employee._id} className="p-3 border-b border-gray-100 last:border-b-0">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.employees.includes(employee._id)}
                          onChange={() => handleEmployeeSelection(employee._id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {employee.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {typeof employee.job === 'object' ? employee.job?.name : (jobsMap[employee.job] || employee.job || 'N/A')} • {typeof employee.branch === 'object' ? employee.branch?.name : (branchesMap[employee.branch] || employee.branch || 'N/A')}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
              
              {errors.employees && (
                <p className="text-red-500 text-sm mt-1">{errors.employees}</p>
              )}
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || availableEmployees.length === 0}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <InlineLoader size="sm" />}
              <span>Assign {formData.employees.length > 1 ? 'Employees' : 'Employee'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssignShiftModal
